// Font awesome
import { faEye } from "@fortawesome/free-solid-svg-icons";
// Fhir front library
import { SearchableTable } from "@fyrstain/fhir-front-library";
// Translation
import i18n from "i18next";
// React
import { FunctionComponent, useCallback } from "react";
// Navigation
import { useNavigate } from "react-router-dom";
// Components
import PandoraPage from "../../components/PandoraPage/PandoraPage";
import { useEnvironment } from "src/context/EnvironmentContext";

const AuditEventExchanges: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////
  const { currentEnvironment } = useEnvironment();

  const navigate = useNavigate();

  const onDetails = useCallback((id: string) => {
    navigate("/AuditEventExchangeDetails/" + id);
  }, [navigate]);


  //////////////////////////////
  //           Error          //
  //////////////////////////////

  const onError = useCallback((error?: unknown) => {
    navigate("/Error", { state: { error } });
  }, [navigate]);

  //////////////////////////////
  //         Functions        //
  //////////////////////////////

  /**
   * Get Method Response to populate the table.
   *
   * @param auditEvent the auditEvent resource.
   */
  function getMethod(auditEvent: any): string {
    if (auditEvent?.action === "C") return "POST";
    if (auditEvent?.action === "R") return "GET";
    if (auditEvent?.action === "U") return "PUT";
    if (auditEvent?.action === "D") return "DELETE";
    if (auditEvent?.action === "E") return "POST";
    
    return "N/A";
  }

  /**
   * Get Path Response to populate the table.
   *
   * @param auditEvent the auditEvent resource.
   */
  function pickEntityLabel(entity: any): string {
    const route = entity?.detail?.find((detail: any) => detail?.type === "route")?.valueString;
    return route
      ?? entity?.description
      ?? entity?.what?.reference
      ?? entity?.what?.identifier?.value
      ?? entity?.what?.display
      ?? entity?.query
      ?? "N/A";
  }
  
  function getPath(auditEvent: any): string {
    const entity = auditEvent?.entity?.find(
    (e: any) =>
      e?.description ||
      e?.detail?.some((detail: any) => detail?.type === "route") ||
      e?.query ||
      e?.what?.reference ||
      e?.what?.identifier?.value ||
      e?.what?.display
    );

    return entity ? pickEntityLabel(entity) : "N/A";
  }

  /**
   * Get the HTTP response status stored in the AuditEvent entity details.
   *
   * @param auditEvent the auditEvent resource.
   */
  function getHttpStatus(auditEvent: any): string {
    for (const entity of auditEvent?.entity ?? []) {
      const detail = entity?.detail?.find((item: any) => item?.type === "status");
      if (detail) {
        return detail.valueString
          ?? detail.valueCodeableConcept?.text
          ?? detail.valueCodeableConcept?.coding?.[0]?.display
          ?? detail.valueCodeableConcept?.coding?.[0]?.code
          ?? i18n.t("general.notavailable");
      }
    }

    return i18n.t("general.notavailable");
  }

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <PandoraPage titleKey="Messages" needsLogin={true}>
      <SearchableTable
        searchCriteriaProperties={{
          title: i18n.t("title.searchcriteria"),
          submitButtonLabel: i18n.t("button.search"),
          resetButtonLabel: i18n.t("button.reset"),
          language: i18n.t,
          fixedParameters: {
            _elements: "id,action,entity,source,recorded,outcome",
            _sort: "-date",
          },
          inputs: [
            {
              label: i18n.t("label.method"),
              type: "text",
              placeholder: i18n.t("placeholder.method"),
              searchParamsName: "action",
            },
            {
              label: i18n.t("label.path"),
              type: "text",
              placeholder: i18n.t("placeholder.path"),
              searchParamsName: "entity",
            },
          ],
        }}
        paginatedTableProperties={{
          columns: [
            {
              header: i18n.t("label.method"),
              dataField: "method",
              width: "25%",
            },
            {
              header: i18n.t("label.path"),
              dataField: "path",
              width: "40%",
            },
            {
              header: i18n.t("label.httpstatus"),
              dataField: "httpStatus",
              width: "15%",
            },
          ],
          action: [
            {
              icon: faEye,
              onClick: onDetails,
            },
          ],
          mapResourceToData: (resource: any) => {
            return {
              method: getMethod(resource),
              path: getPath(resource),
              httpStatus: getHttpStatus(resource),
              id: resource.id,
            };
          },
          searchProperties: {
            serverUrl: currentEnvironment?.messageUrl ?? process.env.REACT_APP_FHIR_URL_AUDIT ?? "fhir",
            resourceType: "AuditEvent",
          },
          onError: onError,
        }}
      />
    </PandoraPage>
  );
};

export default AuditEventExchanges;
